import React from 'react';
import type { LinkedWorklog } from '../../types/plan';

// 「哪几次会话干过这条任务」—— 任何任务都能有，0..n 条。
// 见 docs/design/worklog-task-links-mockup.html 第 ② 节。

type LinkedWorklogsProps = {
  worklogs: LinkedWorklog[];
};

// 后端存的是英文枚举，中文只在前端映射 —— 与 types/worklog.ts 的
// WORKLOG_TASK_TYPE_LABELS 同一套说法。不 import 那个常量是因为这里的
// task_type 是宽松的 string（TaskPresenter 不保证它在枚举内），
// 用 Record<WorklogTaskType, string> 索引会被 tsc 拒。
const TYPE_LABELS: Record<string, string> = {
  fix: '修复',
  feature: '功能',
  refactor: '重构',
  docs: '文档',
  config: '配置',
  analysis: '分析',
  chore: '杂务',
  other: '其他',
};

// 只到「月-日 时:分」：年份在这个语境里没有信息量（同一条任务上的几次会话
// 几乎总在相近的日子），而秒会把这一行挤长。
function shortWhen(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const LinkedWorklogs: React.FC<LinkedWorklogsProps> = ({ worklogs }) => (
  <div className="mb-[9px]">
    <div className="mb-[7px] flex items-baseline gap-[7px] px-0.5">
      <span className="text-[12.5px] font-semibold">相关日志</span>
      <span className="rounded-full border border-todo-line bg-todo-card px-1.5 py-px font-mono text-[11px] text-todo-muted">
        {worklogs.length}
      </span>
      <span className="ml-auto text-[11px] text-todo-muted">哪几次会话干过它</span>
    </div>

    {worklogs.length === 0 ? (
      // 任务侧留空态而不是整块不渲染：任务是活的，它**会**被推进，
      // 这块空白是在说「这里以后会有东西，是这样填的」。
      // （日志那侧相反，见 WorklogPage.tsx。）
      <div className="rounded-lg border border-dashed border-todo-line2 px-[11px] py-[13px] text-[12px] leading-relaxed text-todo-muted">
        还没有会话关联到这条任务。
        <br />
        AI 写日志时带上这条任务的 id 就会出现在这里。
      </div>
    ) : (
      worklogs.map((worklog) => (
        <a
          key={worklog.slug}
          href={`/worklog/${worklog.slug}`}
          className="mb-[7px] block rounded-lg border border-todo-line bg-todo-card px-[10px] py-2 no-underline last:mb-0 hover:border-todo-line2"
        >
          <div className="mb-0.5 flex items-center gap-1.5">
            <span className="font-mono text-[11px] text-todo-muted">
              {shortWhen(worklog.completed_at)}
            </span>
            <span className="rounded border border-todo-line bg-todo-card2 px-1.5 py-px text-[10.5px] text-todo-ink2">
              {TYPE_LABELS[worklog.task_type] ?? worklog.task_type}
            </span>
            {/* AI 写的标一个 ✦，人自己关的什么都不挂。标但标轻：不标的话人无法
                判断该不该信，标重了又会读成「待确认」—— 而它们是直接生效的。 */}
            {worklog.linked_by === 'agent' && (
              <span className="ml-auto text-[11px] text-todo-accent" title="AI 关联">
                ✦
              </span>
            )}
          </div>
          <div className="text-[12.5px] leading-snug text-todo-ink">{worklog.title}</div>
        </a>
      ))
    )}
  </div>
);

export default LinkedWorklogs;
